import { Phone } from 'lucide-react'
import { useState } from 'react'

import { Skeleton } from '@/components/ui/skeleton'
import { useRecordCalls } from '@/hooks/useCalling'
import { useCallStore } from '@/stores/call.store'
import { formatDateTime } from '@/utils/date'

import { DirectionIcon, DispositionChip, OutcomeBadge, RecordingPlayer, TrackingBadge } from './CallBadges'

/** The record's calls, newest first: who called, how, the result and the disposition. */
export function RecordCallsPanel({ recordId }: { recordId: string }) {
  const [page, setPage] = useState(0)
  const { data, isLoading } = useRecordCalls(recordId, page)
  const openDisposition = useCallStore((s) => s.openDisposition)

  if (isLoading) {
    return (
      <div className="space-y-2 p-1">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
      </div>
    )
  }

  if (!data || data.content.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-neutral-500">
        <Phone className="h-6 w-6 text-neutral-300" />
        No calls on this record yet.
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {data.content.map((call) => (
        <div key={call.id} className="space-y-1.5 rounded-xl border border-neutral-200 bg-white p-3">
          <div className="flex items-center gap-2">
            <DirectionIcon call={call} />
            <span className="text-xs font-semibold text-neutral-800">{call.agentName ?? 'Unassigned'}</span>
            <span className="ml-auto text-[11px] tabular-nums text-neutral-400">{formatDateTime(call.requestedAt)}</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <OutcomeBadge call={call} />
            <TrackingBadge call={call} />
            <DispositionChip call={call} />
            {call.canDispose && call.status === 'ENDED' && (
              <button
                type="button"
                onClick={() => openDisposition(call.id)}
                className="ml-auto text-[11px] font-semibold text-primary-600 hover:underline"
              >
                {call.dispositionId ? 'Change disposition' : 'Add disposition'}
              </button>
            )}
          </div>
          {call.dispositionNote && <p className="text-xs text-neutral-600 whitespace-pre-wrap">{call.dispositionNote}</p>}
          <RecordingPlayer call={call} />
        </div>
      ))}
      {data.totalPages > 1 && (
        <div className="flex items-center justify-between pt-1 text-xs text-neutral-500">
          <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)} className="disabled:opacity-40">
            Newer
          </button>
          <span className="tabular-nums">{page + 1} / {data.totalPages}</span>
          <button
            type="button"
            disabled={page >= data.totalPages - 1}
            onClick={() => setPage(page + 1)}
            className="disabled:opacity-40"
          >
            Older
          </button>
        </div>
      )}
    </div>
  )
}
